import React, { useState } from 'react';
import {
  Select,
  Accordion,
  makeStyles,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  MenuItem,
  Box,
  Dialog,
  DialogContent, DialogTitle,
} from '@material-ui/core';
import { isDate, isMoney, isNumber } from '../../../utils/report-utils';
import {
  chunkArrayToPages, excludeAccordion, excludeHeaders, objToArray, roundTo2Dec,
} from '../../../utils/mobile-table-utils';
import CommonPagination from '../CommonPagination';
import {
  ALMOST_BLACK_COLOR, BLACK_COLOR,
  DODGER_BLUE, NAVY_BLUE_COLOR,
  WHITE_COLOR,
} from '../../../const/color-const';
import { Add, Close, ExpandMore, VerticalAlignBottomOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { bindActionCreators, compose } from 'redux';
import { downloadTransaction, downloadZReport } from '../../../actions/report-actions';
import { getHeaderTranslationForStandardReports } from '../../pages/reports/standard/StandardReportUtils';
import MoneyFormat from '../MoneyFormat';
import { formatTimestampAsDateWithTime } from '../../../utils/dates-utils';
import classNames from 'classnames';

const ODD_ITEM_LIGHT_COLOR = '#fdfdfd';
const EVEN_ITEM_DARK_COLOR = '#232838';

const EVEN_ITEM_LIGHT_COLOR = '#F3F3F3';
const ODD_ITEM_DARK_COLOR = '#1d222f';

const EXPANDED_ITEM_LIGHT_COLOR = '#f0f4fd';
const EXPANDED_ITEM_DARK_COLOR = '#232839';

const ACCORDION_DETAILS_LIGHT = '#32302f';
const ACCORDION_DETAILS_DARK = '#EEECECE5';

const ACCORDION_BORDER_COLOR = '#e2e2e2';

const useStyles = makeStyles(theme => ({
  mobilePagination: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(3, 0, 3, 0),
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center',
    },
    '& ul': {
      justifyContent: 'center',
    },
  },
  itemsPerPage: {
    fontSize: 12,
    display: 'flex',
    justifyContent: 'center',
    marginRight: theme.spacing(3),
    '& > div': {
      width: '100%',
      display: 'flex',
      textAlign: 'center',
      alignItems: 'center',
      borderBottom: 'none',
      flexDirection: 'row',
      justifyContent: 'center',
      '& > div': {
        height: 26,
        width: 65,
        fontSize: 14,
        marginTop: 0,
        borderRadius: 4,
        backgroundColor: theme.palette.type === 'light' ? EVEN_ITEM_LIGHT_COLOR : 'transparent',
        '&:before': {
          display: 'none',
        },
      },
    },
  },
  itemsPerPageText: {
    margin: 0,
    fontSize: 12,
  },
  mobileTableWrapper: {
    backgroundColor: theme.palette.type === 'light' ? WHITE_COLOR : ALMOST_BLACK_COLOR,
    width: '100%',
  },
  mobileTable: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
    width: '100%',
    fontSize: 14,
    minHeight: 56,
    margin: theme.spacing(1, 0, 1, 0),
    '&:before': {
      display: 'none',
    },
    '& > div:first-of-type': {
      position: 'relative',
      borderTop: theme.palette.type === 'light' ? `1px solid ${ACCORDION_BORDER_COLOR}` : '1px solid black',
      borderBottom: theme.palette.type === 'light' ? `1px solid ${ACCORDION_BORDER_COLOR}` : '1px solid black',
      '& > div': {
        margin: 0,
      },
      '& > div:last-of-type': {
        position: 'absolute',
        padding: 10,
        margin: 0,
        right: 0,
      },
    },
    '& svg': {
      color: DODGER_BLUE,
      borderRadius: 25,
    },
    '&:first-child': {
      marginTop: 0,
    },
    '&:nth-child': {
      '&:before': {
        display: 'none',
      },
    },
    '&:nth-child(even)': {
      backgroundColor: theme.palette.type === 'light' ? EVEN_ITEM_LIGHT_COLOR : EVEN_ITEM_DARK_COLOR,
    },
    '&:nth-child(odd)': {
      backgroundColor: theme.palette.type === 'light' ? ODD_ITEM_LIGHT_COLOR : ODD_ITEM_DARK_COLOR,
    },
    '& > .Mui-expanded': {
      backgroundColor: theme.palette.type === 'light' ? EXPANDED_ITEM_LIGHT_COLOR : EXPANDED_ITEM_DARK_COLOR,
      '& > div:last-of-type': {
        position: 'absolute',
        right: 0,
      },
    },
  },
  mobileTablePopup: {
    cursor: 'pointer',
    '& div[id*="header"]': {
      border: 'none',
      '& > div': {
        borderTop: theme.palette.type === 'light' ? `1px solid ${ACCORDION_BORDER_COLOR}` : '1px solid black',
        borderBottom: theme.palette.type === 'light' ? `1px solid ${ACCORDION_BORDER_COLOR}` : '1px solid black',
      },
    },
  },
  openIcon: {
    position: 'absolute',
    fontSize: 20,
    right: 10,
    top: 18,
  },
  mobileTableItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1, 1, 0, 0),
    flexWrap: 'wrap',
    maxWidth: '48%',
    width: '100%',
    alignItems: 'flex-start',
  },
  mobileItemKey: {
    fontSize: 12,
    fontWeight: 'bold',
    padding: theme.spacing(0, 0, 0.4, 0),
    margin: 0,
  },
  mobileItemValue: {
    fontSize: 14,
    lineHeight: '14px',
    padding: 0,
    margin: 0,
    wordBreak: 'break-word',
  },
  accordionHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    padding: theme.spacing(1.2, 0, 1.2, 0),
    width: '100%',
    '& > div': {
      padding: theme.spacing(0, 1, 0, 0),
    },
  },
  accordionDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: theme.palette.type === 'light' ? EXPANDED_ITEM_LIGHT_COLOR : EXPANDED_ITEM_DARK_COLOR,
    color: theme.palette.type === 'light' ? ACCORDION_DETAILS_LIGHT : ACCORDION_DETAILS_DARK,
    padding: theme.spacing(2),
    '& button': {
      '& svg': {
        color: 'green',
      },
    },
  },
  popupDetails: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: theme.palette.type === 'light' ? WHITE_COLOR : EXPANDED_ITEM_DARK_COLOR,
    color: theme.palette.type === 'light' ? ACCORDION_DETAILS_LIGHT : ACCORDION_DETAILS_DARK,
    '& button': {
      '& svg': {
        color: 'green',
      },
    },
    '& div[class*="MuiBox-root"]': {
      maxWidth: '50%',
      width: '100%',
      padding: theme.spacing(1.5),
      borderBottom: theme.palette.type === 'light' ? `1px solid ${ACCORDION_BORDER_COLOR}` : '1px solid black',
      '&:nth-child(4n+1), &:nth-child(4n+2)': {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(0),
        backgroundColor: theme.palette.type === 'light' ? EVEN_ITEM_LIGHT_COLOR : EVEN_ITEM_DARK_COLOR,
      },
      '&:nth-child(4n+3), &:nth-child(4n+4)': {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(0),
        backgroundColor: theme.palette.type === 'light' ? ODD_ITEM_LIGHT_COLOR : ODD_ITEM_DARK_COLOR,
      },
      '&:last-of-type': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.palette.type === 'light' ? EVEN_ITEM_LIGHT_COLOR : EVEN_ITEM_DARK_COLOR,
        textAlign: 'center',
        maxWidth: '100%',
        width: '100%',
        fontWeight: 'bold',
        height: 52,
        '& button': {
          padding: theme.spacing(1.5),
          fontWeight: 'bold',
        },
      },
    },
  },
  extraPaginationWrapper: {
    marginBottom: 10,
  },
  dialogWrapper: {
    overflow: 'visible',
    '& div[class*="MuiDialog-paper"]': {
      margin: 24,
    },
  },
  dialogContent: {
    padding: '0 !important',
  },
  dialogTitle: {
    height: 58,
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.type === 'light' ? NAVY_BLUE_COLOR : BLACK_COLOR,
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1.5, 0, 1.5),
    '& h2': {
      width: '100%',
      fontSize: 14,
      color: 'white',
      display: 'flex',
      fontWeight: 'bold',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '& svg': {
      cursor: 'pointer',
    },
  },
}));

const renderFormattedValueForAccordion = (value, key) => {
  switch (true) {
    // eslint-disable-next-line default-case
    case isNumber(value) && key !== 'Id':
      return Math.round(value);
    case isMoney(value):
      return <MoneyFormat value={value} />;
    case isDate(key):
      return formatTimestampAsDateWithTime(Date.parse(value));
    default:
      return value;
  }
};

const MobilePaginationTable = ({
                                 rows,
                                 columns,
                                 reportName,
                                 setDownloadProgress,
                                 downloadZReport,
                                 downloadTransaction,
                                 components,
                                 limitsPerPage,
                                 isAccordion = true,
                               }) => {
  
  const mobileColumns = columns.map(item => item.field);
  
  const filteredRows = rows.map(row => {
    const filteredRow = {};
    for (const [key, value] of Object.entries(row)) {
      if (key === 'Id' || mobileColumns.includes(key)) {
        filteredRow[key] = value;
      }
    }
    return filteredRow;
  });
  
  const [page, setPage] = useState(0);
  const [limitPerPage, setLimitPerPage] = useState(100);
  const { length: totalLength } = filteredRows;
  const totalPages = Math.ceil(totalLength / limitPerPage);
  const currentPages = chunkArrayToPages(filteredRows, limitPerPage);
  const { Pagination: CustomPagination } = components;
  const handleChange = (event) => setLimitPerPage(event.target.value);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { formatMessage } = useIntl();
  const classes = useStyles();
  
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };
  
  const handleAccordionClick = (item, reportName) => {
    if (reportName === 'Transactions') {
      setIsDialogOpen(true);
      setSelectedItem(item);
    }
  };
  
  function renderItem(classes, formatMessage, value, key) {
    return (
      <React.Fragment key={`${value}_${key}`}>
        <Box className={classes.mobileTableItem}>
          <p className={classes.mobileItemKey}>{getHeaderTranslationForStandardReports(formatMessage, key)}:</p>
          <p className={classes.mobileItemValue}>{renderFormattedValueForAccordion(value, key)}</p>
        </Box>
      </React.Fragment>
    );
  }
  
  function renderSwitch(classes, formatMessage, key, value) {
    // eslint-disable-next-line default-case
    switch (typeof value) {
      case 'number':
        return renderItem(classes, formatMessage, roundTo2Dec(value), key);
      case 'string':
        return renderItem(classes, formatMessage, value, key);
    }
  }
  
  function renderPopupData(selectedItem, classes, formatMessage) {
    if (!selectedItem) return;
    const data = objToArray(selectedItem);
    return data.map(([key, value]) => renderSwitch(classes, formatMessage, key, value));
  }
  
  function renderMobilePagination() {
    return <div className={classes.mobilePagination}>
      {CustomPagination && <CustomPagination />}
      <Box display={'flex'}>
        <CommonPagination
          showDetails={false}
          size={'small'}
          page={page + 1}
          siblingCount={0}
          pageCount={totalPages}
          onChange={(event, value) => {
            setPage(value - 1);
          }}
        />
        
        <Box className={classes.itemsPerPage}>
          <FormControl>
            <p className={classes.itemsPerPageText}>Items per page: &nbsp;</p>
            <Select
              value={limitPerPage}
              label="limitPerPage"
              onChange={handleChange}>
              {limitsPerPage.map((limit, index) => {
                return <MenuItem key={index} value={limit}>{limit}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </div>;
  }
  
  function renderHeaders(data, classes, formatMessage, reportName) {
    const headers = excludeHeaders(data, reportName);
    
    return (
      <div className={classes.accordionHeader}>
        {headers.map(([key, value]) => renderItem(classes, formatMessage, value, key))}
      </div>
    );
  }
  
  function renderAccordionData(data, classes, formatMessage, reportName) {
    return excludeAccordion(data, reportName).map(([key, value]) => renderSwitch(classes, formatMessage, key, value));
  }
  
  function renderDownloadButton(reportName, item) {
    if (!item) return;
    // eslint-disable-next-line default-case
    switch (reportName) {
      case 'Transactions':
        const { Id } = item;
        return <Box key={Id}>
          <FormattedMessage
            id="download"
            defaultMessage="Download PDF"
          />
          <IconButton
            onClick={downloadTransaction.bind(this, setDownloadProgress, Id)}>
            <VerticalAlignBottomOutlined />
          </IconButton>
        </Box>;
      case 'ZReport': {
        const { Number, Id, machineID } = item;
        return <Box>
          <FormattedMessage
            id="download"
            defaultMessage="Download"
          />
          <IconButton
            onClick={downloadZReport.bind(this, setDownloadProgress, Number, Id, machineID)}>
            <VerticalAlignBottomOutlined />
          </IconButton>
        </Box>;
      }
    }
  }
  
  function renderAccordion(item) {
    const data = objToArray(item);
    return <Accordion key={item.id || item.Id} className={classes.mobileTable}>
      <AccordionSummary
        onClick={() => handleAccordionClick(item, reportName)}
        expandIcon={reportName === 'Transactions' ? <Add /> : <ExpandMore />}
        aria-controls="content"
        id="header">
        {renderHeaders(data, classes, formatMessage, reportName)}
      
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {renderAccordionData(data, classes, formatMessage, reportName)}
        {renderDownloadButton(reportName, item)}
      </AccordionDetails>
    </Accordion>;
  }
  
  function renderPopupList(item) {
    const data = objToArray(item);
    return <div key={item.id || item.Id} className={classNames(classes.mobileTable, classes.mobileTablePopup)}>
      <div onClick={() => handleAccordionClick(item, reportName)} id="header">
        {renderHeaders(data, classes, formatMessage, reportName)}
        <Add className={classes.openIcon} />
      </div>
    </div>;
  }
  
  function renderDialog() {
    return (
      <Dialog className={classes.dialogWrapper} open={isDialogOpen} onClose={handleCloseDialog}
              BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0, 0.7)' } }}>
        <DialogContent className={classes.dialogContent}>
          <DialogTitle className={classes.dialogTitle}>
            <p>{formatMessage({ id: 'report.sie4.number', defaultMessage: 'Transaction' })} {selectedItem?.Number}</p>
            <Close onClick={handleCloseDialog} />
          </DialogTitle>
          <div className={classes.popupDetails}>
            {selectedItem && renderPopupData(selectedItem, classes, formatMessage)}
            {selectedItem && renderDownloadButton(reportName, selectedItem)}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  return <>
    {renderMobilePagination()}
    
    {columns.length !== 0 && <div className={classes.mobileTableWrapper}>
      {currentPages[page].map((item) => {
        return isAccordion ? renderAccordion(item) : renderPopupList(item);
      })}
    </div>}
    
    {renderDialog()}
    
    {renderMobilePagination()}
  </>;
};

MobilePaginationTable.defaultProps = {
  limitsPerPage: [25, 50, 75, 100],
};

const decorate = compose(
  connect(null, dispatch => bindActionCreators({ downloadZReport, downloadTransaction }, dispatch)));
export default decorate(MobilePaginationTable);